import {
  calculateAnnualDebtService,
  calculateDSCR,
  calculateNOI
} from '@/services/calculations/dscr';
import { FieldConfig, OutputConfig, SectionConfig } from '../types/common';
import z from 'zod';
import { formatCurrency, formatPercentage } from '@/services/string';

const DSCRInputBaseSchema = z.object({
  rentalIncome: z
    .number({ message: 'Rental income is required' })
    .min(0, { message: 'Rental income must be at least $0' }),
  taxes: z
    .number({ message: 'Taxes are required' })
    .min(0, { message: 'Taxes must be at least $0' }),
  insurance: z
    .number({ message: 'Insurance is required' })
    .min(0, { message: 'Insurance must be at least $0' }),
  other: z
    .number({ message: 'Other expenses are required' })
    .min(0, { message: 'Other expenses must be at least $0' }),
  paymentType: z.enum(['Interest Only', 'Fully Amortizing'], {
    message: 'Amortization type is required'
  }),
  loanAmount: z
    .number({ message: 'Loan amount is required' })
    .min(1, { message: 'Loan amount must be at least $1' }),
  interestRate: z.coerce
    .number({ message: 'Interest rate is required' })
    .min(0.01, { message: 'Interest rate must be at least 0.01%' })
    .max(100, { message: 'Interest rate cannot exceed 100%' }),
  amortizationTerm: z.coerce.number().optional()
});

export type DSCRInput = z.infer<typeof DSCRInputBaseSchema>;

export const DSCRKeys = DSCRInputBaseSchema.keyof().options;

export const DSCRInputSchema = DSCRInputBaseSchema.refine(
  (data) => {
    if (data.paymentType === 'Fully Amortizing') {
      return data.amortizationTerm !== undefined && data.amortizationTerm > 0;
    }
    return true;
  },
  {
    message: 'Amortization term is required for amortized loans',
    path: ['amortizationTerm']
  }
);

export const DSCRSectionConfig: SectionConfig<DSCRInput> = [
  {
    title: 'NOI (Net operating income)',
    fields: ['rentalIncome', 'taxes', 'insurance', 'other']
  },
  {
    title: 'Annual debt service',
    fields: ['paymentType', 'loanAmount', 'interestRate', 'amortizationTerm']
  }
];

export const getDSCRFieldConfig = (
  data: DSCRInput
): FieldConfig<DSCRInput> => ({
  rentalIncome: {
    name: 'rentalIncome',
    type: 'currency',
    label: 'Rental income (Monthly)',
    placeholder: '1,500.00'
  },
  taxes: {
    name: 'taxes',
    type: 'currency',
    label: 'Taxes (Annual)',
    placeholder: '2,000.00'
  },
  insurance: {
    name: 'insurance',
    type: 'currency',
    label: 'Insurance (Annual)',
    placeholder: '2,500.00'
  },
  other: {
    name: 'other',
    type: 'currency',
    label: 'Other (HOA, maintenance, etc.) (Monthly)',
    placeholder: '100.00'
  },
  paymentType: {
    name: 'paymentType',
    type: 'toggle-group',
    label: 'Amortization type',
    options: [
      { label: 'Interest only', value: 'Interest Only' },
      { label: 'Amortized', value: 'Fully Amortizing' }
    ]
  },
  loanAmount: {
    name: 'loanAmount',
    type: 'currency',
    label: 'Loan amount',
    placeholder: '200,000.00'
  },
  interestRate: {
    name: 'interestRate',
    type: 'percentage',
    label: 'Interest rate',
    placeholder: '6.00'
  },
  amortizationTerm: {
    name: 'amortizationTerm',
    type: 'number',
    label: 'Amortization term',
    placeholder: '360',
    suffix: 'months',
    hide: data.paymentType === 'Interest Only'
  }
});
export const getDSCROutputConfig = (data: DSCRInput): OutputConfig => {
  const NOI = calculateNOI({
    rentalIncome: data.rentalIncome,
    taxes: data.taxes,
    insurance: data.insurance,
    other: data.other
  });

  const annualDebtService = calculateAnnualDebtService({
    loanAmount: data.loanAmount,
    interestRate: data.interestRate,
    amortizationTerm: data.amortizationTerm || 0,
    amortizationType: data.paymentType
  });

  const dscr = calculateDSCR({
    NOI,
    annualDebtService
  });

  return [
    {
      fields: [
        {
          label: 'NOI (Net Operating Income)',
          value: formatCurrency(NOI)
        },
        {
          label: 'Annual Debt Service',
          value: formatCurrency(annualDebtService)
        },
        {
          label: 'DSCR',
          value: formatPercentage(dscr).replace('%', '')
        }
      ]
    }
  ];
};
